<template>
  <div class="variation-selection__delivery-selection">
    <h3 v-if="showDeliveryOptions">
      Choose delivery
    </h3>
    <product-delivery-selector v-if="showDeliveryOptions" selector-size="small" @selected-delivery-changed="updateDeliveryMethod" />
    <span class="variation-selection__subtext" v-if="selectedDeliveryOption === 'subscription'">Minimum subscription term: 3 years.</span>
  </div>
  <ul class="variation-selection">
    <li v-for="variation in productVariationOptions()" :key="variation.databaseId" class="variation-selection__list-item">
      <u-i-image-handler
        v-if="variation.image"
        class="variation-selection__image"
        :img-src="variationImage(variation)"
        img-width="100px"
        img-height="100px"
        img-format="avif"
        img-densities="1x 2x" />
      <div class="variation-selection__name-wrapper">
        <span class="variation-selection__name" v-html="variation.variationName" />
        <product-sale-badge
          v-if="getDisplayPrice(variation, 'percentageDiscount')"
          class="variation-selection__badge"
          :percentage-discount="getDisplayPrice(variation, 'percentageDiscount')"
          :show-ends-soon="getDisplayPrice(variation, 'showEndsSoon')"
          :show-ends-today="getDisplayPrice(variation, 'showEndsTodayBadge')" />
      </div>
      <span v-if="variation.meta" class="variation-selection__meta">
        {{ variation.meta }}
      </span>
      <template v-if="getDisplayPrice(variation, 'salePrice')">
        <span class="variation-selection__price variation-selection__price--reduced" v-html="getDisplayPrice(variation, 'salePrice')" />
        <span class="variation-selection__old-price" v-html="getDisplayPrice(variation, 'price')" />
      </template>
      <span v-else class="variation-selection__price" v-html="getDisplayPrice(variation, 'price')" />

      <button
        class="variation-selection__buy"
        :class="buyButtonClass(variation)"
        @click.stop="addToCart(variation)">
        <icon-check v-if="variationInCart(variation)" />
        <icon-shopping-cart v-else />
        {{ buyButtonText(variation) }}
      </button>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { useCartStore } from "~/stores/cart";
import { usePriceStore } from "~/stores/priceStore";
import { useUserStore } from "~/stores/user";
import { defineAsyncComponent, ref, computed, watch, onMounted } from "#imports";

const IconShoppingCart = defineAsyncComponent(() => import('~/assets/icons/IconShoppingCart.svg'));
const IconCheck = defineAsyncComponent(() => import('~/assets/icons/IconCheck.svg'));
const cartStore = useCartStore();
const priceStore = usePriceStore();
const userStore = useUserStore();

const emits = defineEmits(['on-selection']);

const loading = ref<number|null>(null);
const variationPrices = ref<Map<number, any>>(new Map());
const currentCurrency = computed(() => userStore.getSelectedCurrency);

const props = defineProps<{
  productVariations: Array,
  productSubscriptionOptions: Object,
  productDetails: {
    type: [Object, null],
    required: false,
  },
}>();

const selectedDeliveryOption = ref('buy-out')

function updateDeliveryMethod(selectedOption:string) {
  selectedDeliveryOption.value = selectedOption
}

const isSubscriptionSelected = computed(() => selectedDeliveryOption.value === 'subscription');

// check if any variation has a subscription option
const showDeliveryOptions = computed(() => props.productSubscriptionOptions?.duration || !! props.productVariations.find((variation) => variation?.productSubscriptionOptions?.duration))

const showExTax = computed(() => userStore.showExTax || ! userStore.getShowProductTax )

// Fetch prices for all variations when component mounts or currency changes
async function fetchVariationPrices(refresh = false) {
  if (process.server) return;

  for (const variation of productVariationOptions()) {
    // Refresh only on first variation to clear stale prices
    const isFirstVariation = ! props.productVariations || props.productVariations[0].databaseId === variation.databaseId;
    const shouldRefresh = refresh && isFirstVariation;
    const priceData = await priceStore.getPrice(variation.databaseId, shouldRefresh);
    if (priceData) {
      variationPrices.value.set(variation.databaseId, priceData);
    }
  }
}

// Watch for currency changes
watch(currentCurrency, () => {
  fetchVariationPrices(true); // Refresh cache on currency change
});

// Fetch prices on mount (refresh to get latest)
onMounted(() => {
  fetchVariationPrices(true);
});

/**
 * If standard product has delivery options for 'subscription | buy-out'
 * Remap delivery options to show as variations in popup selector
 */
function productVariationOptions() {
  if (props.productVariations) {
    return props.productVariations
  }

  const deliveryOptions: Array = []

  // Populate variation popup with available delivery option toggle
  if (props.productSubscriptionOptions && props.productDetails) {
    deliveryOptions.push({
      image: {
        mediaItemUrl: props.productDetails?.productImage,
      },
      databaseId: props.productDetails.productId,
      variationName: '',
      meta: '',
    })
  }

  return deliveryOptions
}

// Get display price for a variation (with currency-aware fallback)
function getDisplayPrice(variation, field: 'price' | 'salePrice' | 'percentageDiscount' | 'showEndsSoon' | 'showEndsTodayBadge') {
  let displayPrice = null;
  let priceData = variationPrices.value.get(variation.databaseId);
  priceData = isSubscriptionSelected.value && priceData?.productSubscriptionOptions ? priceData.productSubscriptionOptions : priceData;

  console.log('priceData', priceData)
  if (priceData) {
    // Use fresh price data from API
    if (field === 'price') {
      if (showExTax) {
        displayPrice = priceData.oldExTax || priceData.currentExTax;
      } else {
        displayPrice = priceData.old || priceData.current;
      }
    } else if (field === 'salePrice') {
      if (showExTax) {
        displayPrice = priceData.saleExTax;
      } else {
        displayPrice = priceData.sale;
      }
    } else if (field === 'percentageDiscount') {
      displayPrice = priceData.percentageDiscount ? `${priceData.percentageDiscount}%` : null;
    } else if (field === 'showEndsSoon') {
      // Keep using SSR data for time-based badges since they don't change with currency
      displayPrice = variation.productSaleBadges?.showEndsSoonBadge || false;
    } else if (field === 'showEndsTodayBadge') {
      displayPrice = variation.productSaleBadges?.showEndsTodayBadge || false;
    }

    if (displayPrice) {
      return isSubscriptionSelected.value ? `${displayPrice} / per year` : displayPrice
    }
  }

  const variationPriceData = variation?.productSubscriptionOptions && isSubscriptionSelected.value ? variation.productSubscriptionOptions : variation

  // Fallback to SSR data
  if (field === 'price') {
    if (showExTax) {
      displayPrice = variationPriceData.priceExTax;
    } else {
      displayPrice = variationPriceData.price;
    }
  } else if (field === 'salePrice') {
    if (showExTax) {
      displayPrice = variationPriceData.salePriceExTax;
    } else {
      displayPrice = variationPriceData.salePrice;
    }
  } else if (field === 'percentageDiscount') {
    displayPrice = variationPriceData.productSaleBadges?.percentageSaleBadge;
  } else if (field === 'showEndsSoon') {
    displayPrice = variationPriceData.productSaleBadges?.showEndsSoonBadge || false;
  } else if (field === 'showEndsTodayBadge') {
    displayPrice = variationPriceData.productSaleBadges?.showEndsTodayBadge || false;
  }

  if (displayPrice) {
    return isSubscriptionSelected.value ? `${displayPrice} / per year` : displayPrice
  }

  return null;
}

async function addToCart (variation): void {
  let subscriptionScheme = isSubscriptionSelected.value ? props.productSubscriptionOptions?.schemeKey : '0';
  await cartStore.addToCart(variation.databaseId, 1, subscriptionScheme);
  emits('on-selection');
}

function variationImage (variation): string {
  return variation.image
    ? variation.image.mediaItemUrl
    : props.productDetails.featuredImage.mediaItemUrl;
}

function variationInCart (variation): boolean {
  return cartStore.getCartVariationIds.includes(variation.databaseId);
}

function buyButtonClass (variation): Record<string, boolean> {
  return {
    'variation-selection__buy--in-cart': variationInCart(variation),
  };
}

function buyButtonText (variation): string {
  return variationInCart(variation)
    ? 'Added to cart'
    : 'Add to cart';
}
</script>

<style lang="scss">
@use "~/common/scss/button";
@use "~/common/scss/helpers";
@use "~/common/scss/variables";

.variation-selection {
  display: flex;
  flex-flow: column;
  gap: .75rem;

  &__delivery-selection {
    text-align: left;
    margin-bottom: 1rem;
  }

  &__subtext {
    padding-top: 0.3em;
    display: block;
    text-align: right;
  }

  &__list-item {
    background-color: variables.$colorGreyED;
    border-radius: 4px 4px;
    display: grid;
    gap: .25rem .75rem;
    grid-template: "image name name" auto "image meta meta" auto "image oldPrice price" auto "image buy buy" auto / #{helpers.px-to-rem(100)} auto auto;
    padding: .5rem .5rem;
    position: relative;
  }

  &__name-wrapper {
    align-items: center;
    display: flex;
    gap: .5rem;
    grid-area: name;
  }

  &__image {
    grid-area: image;
    height: auto;
    max-height: 100%;
    object-fit: contain;
    width: 100%;
  }

  &__name {
    font-weight: bold;
  }

  &__badge {
    flex-shrink: 0;
    font-size: 0.75rem;
    position: static;
    transform: scale(0.8);
    transform-origin: left center;
  }

  &__meta {
    grid-area: meta;
  }

  &__old-price {
    text-decoration: line-through;
    grid-area: oldPrice;
    text-align: right;
  }

  &__price {
    grid-area: price;
    text-align: right;

    &--reduced {
      color: var(--color-orange);
    }
  }

  &__buy {
    @include button.buttonOrange();
    @include button.buttonMedium();

    gap: .5rem;
    grid-area: buy;

    &--in-cart {
      @include button.buttonGreen();

      gap: .25rem;
    }
  }
}
</style>
